import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Lock, RotateCcw, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { emails as api } from '@/api/endpoints'
import { Alert, Modal, Spinner } from '@/components/ui'
import { StatusBadge } from '@/features/emails/status'
import { formatDateTime } from '@/lib/utils'

/** Stavy, z ktorých sa už nikam nepokračuje - opakovať sa dá len z nich. */
const FINAL = ['failed', 'bounced', 'cancelled']
const CANCELLABLE = ['queued', 'sending']

/**
 * Jedna správa: čo v nej bolo, kadiaľ išla a čo sa s ňou dialo.
 *
 * Telo sa zobrazuje tak, ako bolo uložené pri odoslaní — nie prerenderované
 * zo šablóny. Šablóna sa o mesiac zmení a s ňou by sa spätne zmenilo aj to,
 * čo systém tvrdí, že poslal.
 */
export default function EmailDetailModal({
  emailId,
  onClose,
}: {
  emailId: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [view, setView] = useState<'text' | 'html'>('text')

  const { data: email, isLoading } = useQuery({
    queryKey: ['email', emailId],
    queryFn: () => api.get(emailId),
  })

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['emails'] })
    void qc.invalidateQueries({ queryKey: ['email', emailId] })
    void qc.invalidateQueries({ queryKey: ['email-summary'] })
  }

  const retry = useMutation({
    mutationFn: () => api.retry(emailId),
    onSuccess: refresh,
  })
  const cancel = useMutation({
    mutationFn: () => api.cancel(emailId),
    onSuccess: refresh,
  })

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={email?.subject || t('emails.title')}
    >
      {isLoading || !email ? (
        <div className="flex h-32 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={email.status} />
            <span className="text-xs text-slate-500">
              {t(`emails.kind.${email.kind || 'other'}`, {
                defaultValue: email.kind || '—',
              })}
            </span>
            <span className="text-xs text-slate-500">{email.priority_label}</span>
          </div>

          <dl className="grid gap-x-4 gap-y-2 rounded-md border border-slate-200 p-3 text-sm sm:grid-cols-3">
            <Detail
              label={t('emails.recipient')}
              value={email.to_name ? `${email.to_name} <${email.to_email}>` : email.to_email}
            />
            <Detail label={t('emails.queued')} value={formatDateTime(email.created)} />
            <Detail label={t('emails.sent')} value={formatDateTime(email.sent_at)} />
            <Detail label={t('emails.delivered')} value={formatDateTime(email.delivered_at)} />
            <Detail
              label={t('emails.attempts')}
              value={`${email.attempts} / ${email.max_attempts}`}
            />
            <Detail label={t('emails.provider')} value={email.provider} />
            {/* Identifikátor u poskytovateľa. Podľa neho sa správa dohľadá
                v jeho logoch a podľa neho k nej priradíme webhook. */}
            <Detail label={t('emails.messageId')} value={email.message_id} wide />
          </dl>

          {email.last_error ? (
            <Alert variant="error">
              <span className="flex items-start gap-1.5">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                {email.last_error}
              </span>
            </Alert>
          ) : null}

          <section className="overflow-hidden rounded-md border border-slate-200">
            <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
              <h3 className="text-sm font-semibold">{t('emails.body')}</h3>
              {email.body_html ? (
                <div className="ml-auto flex overflow-hidden rounded-md border border-slate-200 text-xs">
                  {(['text', 'html'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setView(mode)}
                      className={
                        view === mode
                          ? 'bg-slate-800 px-2.5 py-1 font-medium text-white'
                          : 'px-2.5 py-1 text-slate-600 hover:bg-slate-100'
                      }
                    >
                      {t(mode === 'text' ? 'emails.bodyText' : 'emails.bodyHtml')}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            {view === 'html' && email.body_html ? (
              <>
                <LockedHtmlPreview html={email.body_html} />
                <p className="flex items-center gap-1.5 border-t border-slate-200 px-3 py-1.5 text-xs text-slate-500">
                  <Lock size={12} />
                  {t('emails.htmlLocked')}
                </p>
              </>
            ) : (
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap p-3 text-sm">
                {email.body_text || email.body_html || '—'}
              </pre>
            )}
          </section>

          <section className="overflow-hidden rounded-md border border-slate-200">
            <h3 className="border-b border-slate-200 px-3 py-2 text-sm font-semibold">
              {t('emails.history')}
            </h3>
            <ul className="divide-y divide-slate-100">
              {email.events.map((event) => (
                <li key={event.id} className="flex flex-wrap gap-2 px-3 py-1.5 text-xs">
                  <span className="w-36 shrink-0 text-slate-500">
                    {formatDateTime(event.occurred_at || event.created)}
                  </span>
                  <span className="font-medium">
                    {t(`emails.event.${event.event}`, { defaultValue: event.event })}
                  </span>
                  {event.detail ? (
                    <span className="text-slate-500">{event.detail}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>

          {retry.isError || cancel.isError ? (
            <Alert variant="error">
              {apiErrorMessage(retry.error ?? cancel.error, t('emails.actionFailed'))}
            </Alert>
          ) : null}

          <div className="flex items-center gap-2">
            {FINAL.includes(email.status) ? (
              <button
                type="button"
                className="btn-primary"
                disabled={retry.isPending}
                onClick={() => retry.mutate()}
              >
                {retry.isPending ? <Spinner /> : <RotateCcw size={14} />}
                {t('emails.retry')}
              </button>
            ) : null}
            {CANCELLABLE.includes(email.status) ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate()}
              >
                {cancel.isPending ? <Spinner /> : <XCircle size={14} />}
                {t('emails.cancel')}
              </button>
            ) : null}
            <button type="button" className="btn-secondary ml-auto" onClick={onClose}>
              {t('global.close')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}

/**
 * Uložené HTML tak, ako ho videl príjemca - ale len na pozeranie.
 *
 * Telo môže obsahovať údaje od klienta, preto nejde do stránky
 * (`dangerouslySetInnerHTML`), ale do `iframe` s prázdnym `sandbox`: bez
 * skriptov, formulárov, vyskakovacích okien a prístupu k aplikácii. CSP
 * pustí len obrázky (logo, QR kód) a vložené štýly. Klikanie na obsah je
 * vypnuté, aby sa z náhľadu omylom neotvoril odkaz na platbu, zrušenie
 * rezervácie a podobne; posúvanie ostáva.
 */
function LockedHtmlPreview({ html }: { html: string }) {
  const guard =
    '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src https: http: data:; style-src \'unsafe-inline\'">' +
    '<style>*{pointer-events:none!important;cursor:default!important;user-select:text}html{pointer-events:auto!important}</style>'
  // Za `<head>`, nie pred celý dokument - pred `<!DOCTYPE>` by prehliadač
  // prepol do quirks režimu a e-mail by sa vykreslil inak než u príjemcu.
  const doc = /<head[^>]*>/i.test(html)
    ? html.replace(/<head[^>]*>/i, (head) => head + guard)
    : guard + html
  return (
    <iframe
      title="email-html-preview"
      sandbox=""
      referrerPolicy="no-referrer"
      srcDoc={doc}
      className="block h-[28rem] w-full bg-white"
    />
  )
}

function Detail({
  label,
  value,
  wide,
}: {
  label: string
  value: string | null | undefined
  wide?: boolean
}) {
  return (
    <div className={wide ? 'sm:col-span-3' : undefined}>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="break-all font-medium">{value || '—'}</dd>
    </div>
  )
}
